// Realmark detail-page parser. Realmark (WA) renders listings server-side
// with no listing JSON (the only JSON-LD is a WebSite node), so this reads
// the page's own listing markup, before the "You may also like these
// properties" carousel (other listings with their own price labels and
// counts) and with HTML comments removed (the template keeps a commented-out
// duplicate of the facts list):
//   <span class="property-price-label sold">Sold In July 2019</span>
//   <h6 class="property-address">41/222 Hay Street, Subiaco WA 6008</h6>
//   <li>…meta-data-bedrooms-blue"></i><p>2 <span>Bedrooms</span></p>   (Bathrooms, Car Spaces)
//   <li>…meta-data-landarea-blue"></i><p> 88  sqm </p>
//   <li>…property-apartment-icon"></i><p>Apartment</p>
//   <p class="name"><a href="…/agent/dave-seah">Dave Seah</a></p>
//   <p class="office">Sales Associate</p><p class="office">Realmark North Coastal</p>
//   initMap: lat: -31.94659700, lng: 115.83111400
//
// Realmark publishes neither a sold price (the price slot is empty) nor a sold
// day: only "Sold In <Month> <Year>". saleDate is the 1st of that month
// ("2019-07-01") and saleDatePrecision: "month" says the day is not the
// page's (George, 2026-10-12). It reaches property_snapshots.sale_date via
// upsert_property_from_queue; the flag stays in raw_data. The building
// area field is not read: the one sample that has it says 22 sqm for a
// three-bedroom house.

import {
  areaValue,
  count,
  decodeEntities,
  formatAddress,
  htmlText,
  propertyTypeLabel,
  stateCode,
  text,
} from "../shared/listing-html.ts";

const ADDRESS_RE = /^(.+),\s*(.+?)\s+([A-Z]{2,3})\s+(\d{4})$/;
const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

function listingHtml(html: string): string {
  const end = html.indexOf("You may also like these properties");
  return (end > 0 ? html.slice(0, end) : html).replace(/<!--[\s\S]*?-->/g, "");
}

function urlListingId(url: string): string | null {
  return url.match(/\/listing\/(\d+)-/)?.[1] ?? null;
}

function metaCount(page: string, icon: string): number | null {
  const match = page.match(new RegExp(`meta-data-${icon}-blue"></i><p>\\s*(\\d+)\\s*<span>`, "i"));
  return match ? count(match[1]) : null;
}

export function parseRealmarkDetailHtml(html: string, url: string): Record<string, unknown> {
  const page = listingHtml(html);
  const addressText = page.match(/<h6 class="property-address">([^<]+)<\/h6>/i);
  const address = addressText ? htmlText(addressText[1]).match(ADDRESS_RE) : null;
  // A challenge page or a page without the listing has no address heading.
  if (!address) return {};
  // The canonical link names the listing id; a different id is a different page.
  const canonicalId = urlListingId(page.match(/<link rel="canonical" href="([^"]+)"/i)?.[1] ?? "");
  const requestedId = urlListingId(url);
  if (canonicalId && requestedId && canonicalId !== requestedId) return {};

  const suburb = address[2].trim();
  const state = stateCode(address[3]);
  const postcode = address[4];
  const hit: Record<string, unknown> = {
    address: formatAddress(address[1].trim(), suburb, state, postcode),
    suburb,
    state,
    postcode,
  };

  const label = page.match(/<span class="property-price-label sold">([^<]*)<\/span>/i);
  const sold = Boolean(label) || /\bstatus-sold\b/.test(page);
  hit.listingType = sold ? "sold" : null;
  const month = label?.[1].match(/Sold In ([A-Za-z]+) (\d{4})/i);
  const monthIndex = month ? MONTHS.indexOf(month[1].toLowerCase()) : -1;
  hit.saleDate =
    month && monthIndex >= 0 ? `${month[2]}-${String(monthIndex + 1).padStart(2, "0")}-01` : null;
  hit.saleDatePrecision = hit.saleDate ? "month" : null;
  hit.salePrice = null;
  hit.price = null;
  const shownPrice = page.match(/<p class="property-price">([^<]*)<\/p>/i);
  hit.priceDisplay =
    text(htmlText(shownPrice?.[1] ?? "")) ?? (label ? text(htmlText(label[1])) : null);

  hit.bedrooms = metaCount(page, "bedrooms");
  hit.bathrooms = metaCount(page, "bathrooms");
  hit.parking = metaCount(page, "carparking");
  const type = page.match(/property-[a-z]+-icon"><\/i>\s*<p>([^<]+)<\/p>/i);
  hit.propertyType = type ? propertyTypeLabel(htmlText(type[1])) : null;

  const land = page.match(
    /meta-data-landarea-blue"><\/i>\s*<p>\s*([\d,.]+)\s*(?:sqm|m2|m²)\s*<\/p>/i,
  );
  hit.landSize = land ? areaValue(land[1]) : null;
  hit.landSizeUnit = hit.landSize ? "sqm" : null;

  const lat = Number(page.match(/\blat:\s*(-?\d+\.\d+)/)?.[1]);
  const lng = Number(page.match(/\blng:\s*(-?\d+\.\d+)/)?.[1]);
  if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
    hit.latitude = lat;
    hit.longitude = lng;
  }

  const agents: string[] = [];
  let office: string | null = null;
  const cardRe =
    /<p class="name"><a href="[^"]*\/agent\/[^"]*">([^<]+)<\/a><\/p>([\s\S]*?)<\/div>/gi;
  for (const card of page.matchAll(cardRe)) {
    const name = decodeEntities(card[1]).trim();
    if (name && !agents.includes(name)) agents.push(name);
    // The first p.office is the agent's role, the last is the office.
    const offices = [...card[2].matchAll(/<p class="office">([^<]*)<\/p>/gi)].map((m) =>
      htmlText(m[1]),
    );
    const last = offices[offices.length - 1];
    if (!office && last && /^Realmark\b/i.test(last)) office = last;
  }
  hit.agentName = agents.length > 0 ? agents.join(", ") : null;
  hit.agencyName = office ?? "Realmark";

  return hit;
}
